import React, { useEffect, useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import './LandingMotion.css';

const PREFERENCE_KEY = 'clair-landing-motion-paused';
const MOTION_EVENT = 'landing-motion-change';
let unavailableStoragePreference = null;

export function readMotionPaused() {
  if (typeof window === 'undefined') return true;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return true;
  if (unavailableStoragePreference !== null) return unavailableStoragePreference;
  try { return window.sessionStorage.getItem(PREFERENCE_KEY) === 'true'; }
  catch { return false; }
}

/** Event-driven enhancements: native scrolling and native pointers stay intact. */
export function useLandingMotion() {
  const rootRef = useRef(null);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    let frame = 0;
    let magnet = null;
    let spotlight = null;
    let depthScene = null;
    let overlayOpen = false;
    let paused = readMotionPaused();
    const cursor = root.querySelector('.landing-context-cursor');
    const cursorLabel = root.querySelector('.landing-context-cursor span');
    const reveals = [...root.querySelectorAll('[data-reveal], [data-clair-reveal]')];
    const sections = [...root.querySelectorAll('[data-motion-section]')];
    const reveal = (element) => element.classList.add('is-revealed', 'is-visible');
    const resetPointer = () => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = 0;
      cursor?.classList.remove('is-active');
      magnet?.style.removeProperty('--magnetic-x');
      magnet?.style.removeProperty('--magnetic-y');
      magnet = null;
      spotlight?.style.removeProperty('--pointer-x');
      spotlight?.style.removeProperty('--pointer-y');
      spotlight = null;
      depthScene?.style.removeProperty('--depth-x');
      depthScene?.style.removeProperty('--depth-y');
      depthScene = null;
    };
    const updatePreference = () => {
      paused = readMotionPaused();
      root.dataset.motion = paused ? 'paused' : 'active';
      if (paused) { reveals.forEach(reveal); resetPointer(); }
    };
    const broadcastPreference = () => {
      updatePreference();
      window.dispatchEvent(new CustomEvent(MOTION_EVENT, { detail: { paused } }));
    };
    const updateVisibility = () => {
      root.dataset.pageHidden = document.hidden ? 'true' : 'false';
      if (document.hidden) resetPointer();
    };
    const updateOverlay = (event) => {
      overlayOpen = Boolean(event.detail?.open);
      root.dataset.mediaOverlay = overlayOpen ? 'open' : 'closed';
      if (overlayOpen) resetPointer();
    };
    updatePreference();
    updateVisibility();
    root.classList.add('has-landing-motion');

    let revealObserver;
    let sectionObserver;
    if ('IntersectionObserver' in window) {
      revealObserver = new IntersectionObserver((entries) => {
        entries.forEach(({ isIntersecting, target }) => {
          if (isIntersecting) { reveal(target); revealObserver.unobserve(target); }
        });
      }, { threshold: 0.06, rootMargin: '0px 0px -24px' });
      reveals.forEach((element) => revealObserver.observe(element));
      sectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting }) => target.classList.toggle('is-in-view', isIntersecting));
      }, { threshold: 0 });
      sections.forEach((element) => sectionObserver.observe(element));
    } else {
      reveals.forEach(reveal);
      sections.forEach((element) => element.classList.add('is-in-view'));
    }

    const onPointerMove = (event) => {
      if (paused || document.hidden || overlayOpen || !finePointer.matches || event.pointerType === 'touch') {
        resetPointer();
        return;
      }
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const labelTarget = target.closest('[data-cursor]');
      const nextSpotlight = target.closest('[data-spotlight]');
      const nextMagnet = target.closest('[data-magnetic]');
      const nextScene = target.closest('[data-depth-scene]');
      if (!labelTarget && !nextSpotlight && !nextMagnet && !nextScene) {
        resetPointer();
        return;
      }
      const { clientX: x, clientY: y } = event;
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        if (magnet && magnet !== nextMagnet) {
          magnet.style.removeProperty('--magnetic-x');
          magnet.style.removeProperty('--magnetic-y');
        }
        magnet = nextMagnet;
        if (spotlight && spotlight !== nextSpotlight) {
          spotlight.style.removeProperty('--pointer-x');
          spotlight.style.removeProperty('--pointer-y');
        }
        spotlight = nextSpotlight;
        if (depthScene && depthScene !== nextScene) {
          depthScene.style.removeProperty('--depth-x');
          depthScene.style.removeProperty('--depth-y');
        }
        depthScene = nextScene;
        if (magnet) {
          const rect = magnet.getBoundingClientRect();
          magnet.style.setProperty('--magnetic-x', `${Math.max(-4, Math.min(4, (x - rect.left - rect.width / 2) * .045))}px`);
          magnet.style.setProperty('--magnetic-y', `${Math.max(-4, Math.min(4, (y - rect.top - rect.height / 2) * .08))}px`);
        }
        if (spotlight) {
          const rect = spotlight.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) {
            spotlight.style.setProperty('--pointer-x', `${Math.max(0, Math.min(100, ((x - rect.left) / rect.width) * 100))}%`);
            spotlight.style.setProperty('--pointer-y', `${Math.max(0, Math.min(100, ((y - rect.top) / rect.height) * 100))}%`);
          }
        }
        if (depthScene) {
          const rect = depthScene.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) {
            depthScene.style.setProperty('--depth-x', `${Math.max(-8, Math.min(8, ((x - rect.left) / rect.width - .5) * -16))}px`);
            depthScene.style.setProperty('--depth-y', `${Math.max(-8, Math.min(8, ((y - rect.top) / rect.height - .5) * -16))}px`);
          }
        }
        if (cursor && cursorLabel) {
          const label = labelTarget?.dataset.cursor;
          cursor.classList.toggle('is-active', Boolean(label));
          if (label) {
            cursorLabel.textContent = label;
            const left = Math.max(8, Math.min(window.innerWidth - 140, x + 20));
            const top = Math.max(8, Math.min(window.innerHeight - 52, y + 20));
            cursor.style.transform = `translate3d(${left}px, ${top}px, 0)`;
          }
        }
      });
    };
    root.addEventListener('pointermove', onPointerMove, { passive: true });
    root.addEventListener('pointerleave', resetPointer);
    // Anchors and wheel input can move the page without another pointermove.
    root.addEventListener('pointerdown', resetPointer, { passive: true });
    root.addEventListener('click', resetPointer);
    document.addEventListener('keydown', resetPointer);
    document.addEventListener('visibilitychange', updateVisibility);
    window.addEventListener(MOTION_EVENT, updatePreference);
    window.addEventListener('landing-media-overlay-change', updateOverlay);
    window.addEventListener('wheel', resetPointer, { passive: true });
    reduced.addEventListener('change', broadcastPreference);
    finePointer.addEventListener('change', resetPointer);
    return () => {
      resetPointer();
      revealObserver?.disconnect();
      sectionObserver?.disconnect();
      root.removeEventListener('pointermove', onPointerMove);
      root.removeEventListener('pointerleave', resetPointer);
      root.removeEventListener('pointerdown', resetPointer);
      root.removeEventListener('click', resetPointer);
      document.removeEventListener('keydown', resetPointer);
      document.removeEventListener('visibilitychange', updateVisibility);
      window.removeEventListener(MOTION_EVENT, updatePreference);
      window.removeEventListener('landing-media-overlay-change', updateOverlay);
      window.removeEventListener('wheel', resetPointer);
      reduced.removeEventListener('change', broadcastPreference);
      finePointer.removeEventListener('change', resetPointer);
    };
  }, []);
  return rootRef;
}

export function LandingMotionControls() {
  const [paused, setPaused] = useState(readMotionPaused);
  const [reduced, setReduced] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  useEffect(() => {
    const sync = () => {
      setPaused(readMotionPaused());
      setReduced(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    };
    window.addEventListener(MOTION_EVENT, sync);
    return () => window.removeEventListener(MOTION_EVENT, sync);
  }, []);
  const toggle = () => {
    const next = !paused;
    try {
      window.sessionStorage.setItem(PREFERENCE_KEY, String(next));
      unavailableStoragePreference = null;
    } catch { unavailableStoragePreference = next; }
    // State remains useful if storage is unavailable; the event carries intent.
    setPaused(next);
    window.dispatchEvent(new CustomEvent(MOTION_EVENT, { detail: { paused: next } }));
  };
  return (
    <>
      <div className="landing-reading-progress" aria-hidden="true"><i /></div>
      <div className="landing-context-cursor" aria-hidden="true"><span /></div>
      <button className="landing-motion-toggle" type="button" onClick={toggle} aria-pressed={paused} disabled={reduced} aria-label={reduced ? 'Movimiento reducido según tu sistema' : paused ? 'Activar animaciones' : 'Pausar animaciones'} title={reduced ? 'Tu sistema tiene activada la preferencia de movimiento reducido' : undefined}>
        {paused ? <Play size={12} aria-hidden="true" /> : <Pause size={12} aria-hidden="true" />}
        <span>{reduced ? 'Movimiento reducido' : paused ? 'Animaciones pausadas' : 'Pausar animaciones'}</span>
      </button>
    </>
  );
}
